import { countClass, currentUser, buildConfigPointer, pointer, pointerId, queryAllClass, queryClass, saveClassRecord, type ParseRecord } from './parse';
import { loadMyCustomer } from './account';

export type TestQuestion = ParseRecord & {
  QuesTitle?: string;
  Opt1?: string;
  Opt2?: string;
  Opt3?: string;
  Opt4?: string;
  Opt5?: string;
  CorrectAnswer?: string[];
  Marks?: number;
  SolutionInfo?: string;
  Type?: string;
  SubjectName?: string;
  SubjectsPtr?: ParseRecord & { SubjectName?: string };
};

export type QueRel = ParseRecord & {
  IndexNo?: number;
  NegativeMarks?: number;
  TestQuestionsPtr?: TestQuestion;
};

export type SubmittedAnswer = {
  SubmittedAnswer: (string | null)[];
  TestQuestionsPtr: { __type: 'Pointer'; className: 'test_TestQuestions'; objectId: string };
  CorrectAnswer: string[];
  ObtainedMarks: number;
};

export type ScoreSummary = {
  obtained: number;
  totalMarks: number;
  percentage: number;
  correct: number;
  wrong: number;
  skipped: number;
  grade: string;
};

const OPTION_COUNT = 5;

export function optionsOf(question: TestQuestion): Array<{ key: number; html: string }> {
  const output: Array<{ key: number; html: string }> = [];
  for (let key = 1; key <= OPTION_COUNT; key += 1) {
    const html = (question as Record<string, unknown>)[`Opt${key}`];
    if (typeof html === 'string' && html.trim()) output.push({ key, html });
  }
  return output;
}

/** Return a usable correct option only when it points to an option present on the question. */
export function correctOptionKey(question: TestQuestion): number | undefined {
  const value = question.CorrectAnswer?.[0];
  if (value === undefined || value === null || value === '') return undefined;
  const key = Number(value);
  return Number.isInteger(key) && optionsOf(question).some((option) => option.key === key) ? key : undefined;
}

/** Load one published test with its ordered question relations. */
export async function loadPublishedTest(testId: string): Promise<{ test: ParseRecord; questions: Array<QueRel & { TestQuestionsPtr: TestQuestion }> } | undefined> {
  const rows = await queryClass('test_TestInfo', {
    limit: 1,
    where: (query) => query.equalTo('objectId', testId),
  });
  const test = rows[0];
  if (!test || test.IsPublished === false) return undefined;
  const rels = await queryAllClass('test_TestQueRel', {
    order: 'IndexNo',
    include: ['TestQuestionsPtr', 'TestQuestionsPtr.SubjectsPtr'],
    // Relation rows predate AppId scoping in the testing replica.
    appScoped: false,
    where: (query) => query.equalTo('TestInfoPtr', pointer('test_TestInfo', testId)),
  });
  const questions = rels.filter((rel) => rel.TestQuestionsPtr?.QuesTitle) as Array<QueRel & { TestQuestionsPtr: TestQuestion }>;
  return { test, questions };
}

function realUserId(): string | undefined {
  const id = pointerId(currentUser());
  return id && id !== 'local-student' ? id : undefined;
}

function gradeFor(percentage: number) {
  if (percentage >= 75) return 'A';
  if (percentage >= 60) return 'B';
  if (percentage >= 40) return 'C';
  return 'D';
}

export function scoreAttempt(questions: QueRel[], answers: Map<number, number>): ScoreSummary {
  let obtained = 0;
  let totalMarks = 0;
  let correct = 0;
  let wrong = 0;
  let skipped = 0;
  questions.forEach((rel, index) => {
    const question = (rel.TestQuestionsPtr ?? {}) as TestQuestion;
    const correctKey = correctOptionKey(question);
    if (correctKey === undefined) return;
    const marks = Number(rel.PositiveMarks ?? question.Marks ?? 0);
    totalMarks += marks;
    const selected = answers.get(index);
    if (selected === undefined) { skipped += 1; return; }
    const isRight = selected === correctKey;
    if (isRight) {
      correct += 1;
      obtained += marks;
    } else {
      wrong += 1;
      const negative = Number(rel.NegativeMarks ?? question.NegativeMarks ?? 0);
      if (negative !== 0) obtained -= negative > 0 ? negative : -negative;
    }
  });
  const percentage = totalMarks ? Math.max(0, Math.round((obtained / totalMarks) * 10000) / 100) : 0;
  return { obtained, totalMarks, percentage, correct, wrong, skipped, grade: gradeFor(percentage) };
}

/** Legacy wire shape shared with the native app's test_TestAnswered.SubmittedAnswers entries. */
export function submittedAnswerEntry(rel: QueRel, selected: number): SubmittedAnswer {
  const array: (string | null)[] = Array.from({ length: OPTION_COUNT }, () => null);
  array[selected - 1] = String(selected);
  const question = (rel.TestQuestionsPtr ?? {}) as TestQuestion;
  const correctKey = correctOptionKey(question);
  const isRight = correctKey !== undefined && selected === correctKey;
  let marks = 0;
  if (isRight) marks = Number(rel.PositiveMarks ?? question.Marks ?? 0);
  else if (correctKey !== undefined) {
    const negative = Number(rel.NegativeMarks ?? question.NegativeMarks ?? 0);
    if (negative !== 0) marks = negative > 0 ? -negative : negative;
  }
  return {
    SubmittedAnswer: array,
    TestQuestionsPtr: { __type: 'Pointer', className: 'test_TestQuestions', objectId: String(question.objectId) },
    CorrectAnswer: [String(correctKey ?? '')],
    ObtainedMarks: marks,
  };
}

/** Persist exam-mode attempt exactly like the native app (answered sheet + result row). */
export async function submitExamAttempt(params: {
  test: ParseRecord;
  questions: QueRel[];
  answers: Map<number, number>;
  score: ScoreSummary;
}): Promise<string | undefined> {
  const userId = realUserId();
  if (!userId) return undefined;
  const entries = [...params.answers.entries()].map(([index, selected]) => submittedAnswerEntry(params.questions[index], selected));
  const answered = await saveClassRecord('test_TestAnswered', {
    CustomerPtr: pointer('_User', userId),
    TestInfoPtr: pointer('test_TestInfo', params.test.objectId),
    SubmittedAnswers: entries,
    CourseTitle: params.test.CourseTitle ?? '',
    IsActive: true,
    SubmitttedDateTime: new Date().toISOString(),
    TestTitle: params.test.TestTitle ?? '',
  });
  const result = await saveClassRecord('test_TestResult', {
    CustomerPtr: pointer('_User', userId),
    TestInfoPtr: pointer('test_TestInfo', params.test.objectId),
    TestAnsweredPtr: pointer('test_TestAnswered', answered.objectId),
    CustName: currentUser()?.name ?? currentUser()?.username ?? '',
    CourseTitle: params.test.CourseTitle ?? '',
    TestDate: new Date().toISOString(),
    TestTitle: params.test.TestTitle ?? '',
    ObtainedMarks: params.score.obtained,
    Percentage: params.score.percentage,
    ResultDesc: '__',
    Status: params.test.Status ?? '',
    TotalMarks: params.score.totalMarks || Number(params.test.TotalMarks ?? 0),
    YourGrade: params.score.grade,
    MaskedPhone: '',
  });
  return result.objectId;
}

export async function loadMyResults(limit = 25): Promise<ParseRecord[]> {
  const userId = realUserId();
  if (!userId) return [];
  return queryClass('test_TestResult', {
    limit,
    order: '-createdAt',
    where: (query) => query.equalTo('CustomerPtr', pointer('_User', userId)),
  });
}

export async function loadResultById(resultId: string): Promise<ParseRecord | undefined> {
  const userId = realUserId();
  if (!userId) return undefined;
  const customer = await loadMyCustomer().catch(() => undefined);
  const identities = [
    pointer('_User', userId),
    ...(customer?.objectId ? [pointer('elearning_Customer', customer.objectId)] : []),
  ];
  const matches = await Promise.all(identities.map(async (identity) => {
    const rows = await queryClass('test_TestResult', {
      limit: 1,
      include: ['TestAnsweredPtr'],
      appScoped: false,
      where: (query) => {
        query.equalTo('objectId', resultId);
        query.equalTo('CustomerPtr', identity);
      },
    });
    return rows[0];
  }));
  return matches.find((row) => row !== undefined);
}

export type LeaderboardEntry = {
  objectId: string;
  learnerName: string;
  percentage: number;
  obtainedMarks: number;
  totalMarks: number;
  isMine: boolean;
  rank: number;
};

/** Best result per learner for each requested test, matching legacy leaderboard behavior. */
export async function loadTestLeaderboards(testInfoIds: string[]): Promise<Map<string, LeaderboardEntry[]>> {
  const ids = [...new Set(testInfoIds.filter(Boolean))];
  if (!ids.length) return new Map();
  const userId = realUserId();
  const customer = await loadMyCustomer().catch(() => undefined);
  const myIds = new Set([userId, customer?.objectId].filter((id): id is string => Boolean(id)));
  const rows = await queryAllClass('test_TestResult', {
    order: '-Percentage,-ObtainedMarks',
    keys: ['CustomerPtr', 'TestInfoPtr', 'CustName', 'Percentage', 'ObtainedMarks', 'TotalMarks', 'TestDate'],
    appScoped: false,
    where: (query) => query.containedIn('TestInfoPtr', ids.map((id) => pointer('test_TestInfo', id))),
  });
  const rowsByTest = new Map<string, ParseRecord[]>();
  for (const row of rows) {
    const testId = pointerId(row.TestInfoPtr);
    if (testId) rowsByTest.set(testId, [...(rowsByTest.get(testId) ?? []), row]);
  }
  const output = new Map<string, LeaderboardEntry[]>();
  for (const [testId, testRows] of rowsByTest) {
    const bestByLearner = new Map<string, ParseRecord>();
    for (const row of testRows) {
      const learnerId = pointerId(row.CustomerPtr) ?? row.objectId;
      const current = bestByLearner.get(learnerId);
      if (!current || Number(row.Percentage ?? 0) > Number(current.Percentage ?? 0)
        || (Number(row.Percentage ?? 0) === Number(current.Percentage ?? 0) && Number(row.ObtainedMarks ?? 0) > Number(current.ObtainedMarks ?? 0))) {
        bestByLearner.set(learnerId, row);
      }
    }
    const sorted = [...bestByLearner.entries()].sort((left, right) =>
      Number(right[1].Percentage ?? 0) - Number(left[1].Percentage ?? 0)
      || Number(right[1].ObtainedMarks ?? 0) - Number(left[1].ObtainedMarks ?? 0)
      || String(left[1].TestDate ?? '').localeCompare(String(right[1].TestDate ?? '')),
    );
    output.set(testId, sorted.map(([learnerId, row], index) => ({
      objectId: row.objectId,
      learnerName: String(row.CustName ?? '').trim() || (myIds.has(learnerId) ? 'You' : 'Learner'),
      percentage: Number(row.Percentage ?? 0),
      obtainedMarks: Number(row.ObtainedMarks ?? 0),
      totalMarks: Number(row.TotalMarks ?? 0),
      isMine: myIds.has(learnerId),
      rank: index + 1,
    })));
  }
  return output;
}

export async function loadTestLeaderboard(testInfoId: string): Promise<LeaderboardEntry[]> {
  const results = await loadTestLeaderboards([testInfoId]);
  return results.get(testInfoId) ?? [];
}

/** Rebuild the saved exam review so result pages opened later still show section totals. */
export async function loadSavedAttemptReview(resultId: string): Promise<{ result: ParseRecord; questions: QueRel[]; answers: Map<number, number> } | undefined> {
  const result = await loadResultById(resultId);
  const testInfoId = pointerId(result?.TestInfoPtr);
  const answered = result?.TestAnsweredPtr as ParseRecord | undefined;
  if (!result) return undefined;
  if (!testInfoId || !answered) return { result, questions: [], answers: new Map() };
  let relations: ParseRecord[];
  try {
    relations = await queryAllClass('test_TestQueRel', {
      order: 'IndexNo',
      include: ['TestQuestionsPtr', 'TestQuestionsPtr.SubjectsPtr'],
      appScoped: false,
      where: (query) => query.equalTo('TestInfoPtr', pointer('test_TestInfo', testInfoId)),
    });
  } catch {
    // Keep the learner's own saved score visible if the source test is no longer available.
    return { result, questions: [], answers: new Map() };
  }
  const submitted = Array.isArray(answered.SubmittedAnswers) ? answered.SubmittedAnswers.flat(Infinity) as ParseRecord[] : [];
  const answers = new Map<number, number>();
  relations.forEach((rel, index) => {
    const questionId = pointerId(rel.TestQuestionsPtr);
    const entry = submitted.find((answer) => pointerId(answer.TestQuestionsPtr) === questionId);
    const selected = Array.isArray(entry?.SubmittedAnswer)
      ? entry.SubmittedAnswer.findIndex((value: unknown) => value !== null && value !== undefined && value !== '') + 1
      : 0;
    if (selected > 0) answers.set(index, selected);
  });
  return { result, questions: relations as QueRel[], answers };
}

/** Exact number of exam results recorded for the signed-in student. */
export async function countMyAttempts(): Promise<number> {
  const userId = realUserId();
  if (!userId) return 0;
  return countClass('test_TestResult', {
    appScoped: false,
    where: (query) => query.equalTo('CustomerPtr', pointer('_User', userId)),
  });
}

export { buildConfigPointer };
